import clsx from 'clsx';
import { get } from 'lodash';
import { useId } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormFieldMessage } from '../message';
import { RequiredSpan } from '../required';

import type { NumberInputProps } from './number.types';
import type { FieldValues } from 'react-hook-form';

import './styles.css';

const NumberInput = <T extends FieldValues>({
  label,
  disabled,
  name,
  placeholder,
  required,
}: NumberInputProps<T>) => {
  const id = useId();

  const {
    register,
    formState: { errors },
  } = useFormContext<T>();

  const error = get(errors, name);

  const hasError = Boolean(error);
  const messageId = `text-input-message-${id}`;

  return (
    <div className="flex flex-col gap-b">
      <label
        className={clsx(['text-h4 block'], {
          'text-error': hasError,
          'text-gray-200': disabled,
        })}
        htmlFor={id}
      >
        {label} {required ? <RequiredSpan /> : null}
      </label>

      <div className="input-wrapper text-body-2 relative flex h-input-sm w-full items-center justify-between truncate rounded-rad-a border-none bg-white text-left ring-1 ring-inset ring-gray-700 transition-all hover:ring-2 hover:ring-blue-500 md:h-input-md lg:h-input-lg">
        <input
          className="text-body-2 peer/input h-full grow border-none bg-transparent px-b py-c [-moz-appearance:_textfield] placeholder:leading-none placeholder:text-gray-200 focus:shadow-none focus:outline-none focus:ring-0 disabled:pointer-events-none disabled:text-gray-200 disabled:ring-gray-200 [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none"
          id={id}
          type="number"
          aria-describedby={messageId}
          aria-invalid={hasError}
          {...(disabled && { disabled })}
          {...(placeholder && { placeholder })}
          {...(required && { 'aria-required': required })}
          {...register(name)}
          data-testid="number input"
        />
      </div>

      <FormFieldMessage
        id={messageId}
        error={error?.message as string}
        {...(disabled && { disabled })}
      />
    </div>
  );
};

export default NumberInput;
